import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useProductNotificationMapping, type ProductNotificationMappingController } from "../../hooks/useProductNotificationMapping";
import type { ProductNotificationMappingView, ProductNotificationSelections } from "../../types/productNotificationMappings";
import AlphaSettingsModal from "../../pages/alpha/AlphaSettingsModal";
import AdminModalShell from "../admin/AdminModalShell";
import ProductNotificationChannelSelector from "./ProductNotificationChannelSelector";
import { hasNotificationChannels, selectionsFromMapping } from "../../utils/productNotificationSelections";

interface Props {
  controller: ProductNotificationMappingController;
  onClose: () => void;
  onSaved: () => void;
  appearance?: "default" | "alpha";
  defaultChannelsOn?: boolean;
}

export default function ProductNotificationMappingDialog({ controller, onClose, onSaved, appearance = "alpha", defaultChannelsOn = false }: Props) {
  const { state, saving, notice, reload } = controller;
  const isAlpha = appearance === "alpha";
  const textClass = `text-sm ${isAlpha ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`;
  const body = <div className="space-y-4">
    {notice && <p role="status" className={textClass}>{notice}</p>}
    {state.status === "loading" && <p role="status" className={textClass}>Loading product notifications...</p>}
    {state.status === "unavailable" && <p role="alert" className={textClass}>Trade alerts are not available for this product. Your access may have changed.</p>}
    {state.status === "error" && <div role="alert" className={`space-y-3 ${textClass}`}>
      <p>Could not load product notifications: {state.message}</p>
      <button type="button" disabled={saving} className="underline disabled:opacity-50" onClick={reload}>Retry product notifications</button>
    </div>}
    {/* A fast refresh may batch loading and ready together. A new revision
        still discards the old draft after reconciliation. */}
    {state.status === "ready" && <SelectionsForm key={state.revision} view={state.value} controller={controller} onClose={onClose} onSaved={onSaved} appearance={appearance} defaultChannelsOn={defaultChannelsOn} />}
  </div>;

  return isAlpha
    ? <AlphaSettingsModal isOpen title="Trade alerts" onClose={onClose} closeDisabled={saving} maxWidthClass="max-w-2xl">{body}</AlphaSettingsModal>
    : <AdminModalShell isOpen title="Trade alerts" onClose={onClose} closeDisabled={saving} maxWidthClassName="max-w-2xl">{body}</AdminModalShell>;
}

function SelectionsForm({ view, controller, onClose, onSaved, appearance, defaultChannelsOn }: Props & { view: ProductNotificationMappingView }) {
  const [selections, setSelections] = useState<ProductNotificationSelections>(() => {
    const saved = selectionsFromMapping(view.mapping);
    // After an uncertain save, show the refreshed server choices instead of defaults.
    if (defaultChannelsOn && !controller.notice && !hasNotificationChannels(saved)) {
      return { shouldEmail: true, shouldWebInbox: true, shouldMobileInbox: true, shouldPush: true };
    }
    return saved;
  });
  const isAlpha = appearance === "alpha";
  const { saving, saveError, save } = controller;
  return <form className="space-y-4" onSubmit={async event => {
    event.preventDefault();
    // The controller returns false for failures and obsolete/unmounted writes.
    if (await save(selections)) onSaved();
  }}>
    <div>
      <h3 className={`break-words text-base font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
        {view.product.title || `Product #${view.product.productId}`}
      </h3>
      <p className={`text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>{view.product.productType === "BASKET" ? "Basket" : "Portfolio"} - {view.product.provider?.name}</p>
    </div>
    <ProductNotificationChannelSelector value={selections} onChange={setSelections} disabled={saving} appearance={appearance} />
    <p className={`text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-gray-400"}`}>Each channel is independent. Choose the channels you want for this product's trade alerts.</p>
    {saveError && <p role="alert" className={`text-sm ${isAlpha ? "text-v7-rust" : "text-red-700 dark:text-red-400"}`}>{saveError}</p>}
    <div className="flex flex-wrap items-center justify-end gap-3">
      {!saving && <Link to="/alpha/notifications?tab=preferences" onClick={onClose}
        className={`mr-auto w-full text-sm underline sm:w-auto ${isAlpha ? "text-v7-bronze-dark" : "text-rp-amber"}`}>Manage product trade alerts</Link>}
      <button type="button" onClick={onClose} disabled={saving}
        className={`border px-4 py-2 text-sm font-semibold disabled:opacity-50 ${isAlpha ? "rounded-v7-control border-v7-ink/30 text-v7-ink" : "rounded-lg border-gray-200 text-gray-700 dark:border-rp-separator dark:text-gray-300"}`}>Cancel</button>
      <button type="submit" disabled={saving}
        className={`px-4 py-2 text-sm font-semibold disabled:opacity-50 ${isAlpha ? "rounded-v7-control bg-v7-ink text-v7-white hover:bg-v7-ink-soft" : "rounded-lg bg-rp-amber text-white hover:bg-yellow-600"}`}>{saving ? "Saving..." : "Save selections"}</button>
    </div>
  </form>;
}

/** The list mounts a fresh editor for each opening; the portfolio button owns its controller. */
export function ProductNotificationMappingEditor({ productId, ...props }: Omit<Props, "controller"> & { productId: number }) {
  const controller = useProductNotificationMapping(productId);
  return <ProductNotificationMappingDialog controller={controller} {...props} />;
}
